import { PATH } from "@/constants/path";
import { Role } from "@/constants/role";
import { UserToken } from "@/Interfaces/userToken";

/**
 * Whether the org dashboards (Pulse, the onboarding report and the HR AI assistant) should
 * be offered to this user (TPT-470/471, TPT-594).
 *
 * Customer HR always: the api resolves their organisation from their employment. The
 * ooca team only once a customer is selected in Corporate List, because that selection
 * (`meta.organization_id` on the token) is what the api reads for them; without it there
 * is no customer to report on.
 *
 * This only decides what to draw. The api enforces the same rule on every Pulse and
 * onboarding request -- see OrgDashboardAccessService in the api repo. The assistant's
 * server-side check is the api's agent allow-list (TPT-492).
 */
export const canReadOrgDashboards = (user: UserToken | null): boolean => {
  if (!user) {
    return false;
  }

  const roles = user.data.roles ?? [];

  if (
    roles.includes(Role.ORGANIZATION_ADMIN) ||
    roles.includes(Role.ORGANIZATION_SUPER_ADMIN)
  ) {
    return true;
  }

  const isInternal =
    roles.includes(Role.SUPERADMIN) || roles.includes(Role.ADMIN);

  return isInternal && Number(user.data.meta?.organization_id) > 0;
};

/** The paths gated by `canReadOrgDashboards`. */
export const ORG_DASHBOARD_PATHS: string[] = [
  PATH.PULSE,
  PATH.ONBOARDING_ASSESSMENT,
  PATH.AI_ASSISTANT,
];
